import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import { LeftChevron } from "@/components/icons";
import apiClient from "@/lib/apiClient";
import { buildJitsiRoomUrl } from "../utils/jitsi";
import type {
  EducationDetailDto,
  ExamAttemptDto,
  CertificateDto,
  ObjectiveDto,
  EnrollmentDto,
} from "../types/client-education-detail.types";
import {
  EnrollmentStatus,
  EducationType,
  ObjectiveCompletionStatus,
} from "../types/client-education-detail.types";
import { clientEducationDetailApi } from "../services/client-education-detail.api";
import { resolveCta, type CtaDescriptor } from "../utils/resolve-cta";
import { resolveAccessMode } from "../utils/access-mode";
import { DetailSidebar } from "../components/DetailSidebar/DetailSidebar";
import { PendingApprovalState } from "../components/empty-states/PendingApprovalState";
import { RejectedState } from "../components/empty-states/RejectedState";
import { CancelledState } from "../components/empty-states/CancelledState";
import { DatePendingState } from "../components/empty-states/DatePendingState";
import { NotCompletedState } from "../components/empty-states/NotCompletedState";
import { HeroSection } from "../components/sections/HeroSection";
import { DescriptionSection } from "../components/sections/DescriptionSection";
import { VirtualClassroomsSection } from "../components/sections/VirtualClassroomsSection/VirtualClassroomsSection";
import { ContentSection } from "../components/sections/ContentSection/ContentSection";
import { ContentPreviewSection } from "../components/sections/ContentPreviewSection";
import { ResourcesSection } from "../components/sections/ResourcesSection";
import { ClassFilesSection } from "../components/sections/ClassFilesSection";
import { AttendanceSection } from "../components/sections/AttendanceSection";
import { ExamSection } from "../components/sections/ExamSection";
import { SurveySection } from "../components/sections/SurveySection";
import { CertificateSection } from "../components/sections/CertificateSection";
import { ClassmatesSection } from "../components/sections/ClassmatesSection";
import { TrainingStages } from "../components/sections/TrainingStages";
import { ActiveStageCard } from "../components/sections/ActiveStageCard";
import styles from "./ClientEducationDetailPage.module.scss";

/**
 * Kursiyer Eğitim Detay Sayfası.
 *
 * Layout mimarisi (wireframe):
 *   DashboardLayout (sol nav daima görünür)
 *   ├─ Breadcrumb + back
 *   ├─ Hero (cover + title + meta) — tam genişlik
 *   ├─ TrainingStages (timeline) — tam genişlik
 *   ├─ 2 kolon grid:
 *   │   ├─ Sticky DetailSidebar (CTA, progress, quick info)
 *   │   └─ Main body (ActiveStageCard + tür bazlı akış + alt bölümler)
 *   └─ Responsive: <1024px tek kolon
 *
 * Tür bazlı akış:
 *   ON_SITE  → SchedulingSection + VirtualClassroomsSection + Attendance
 *   ONLINE   → VirtualClassroomsSection (Jitsi) + Attendance
 *   UNIT     → ContentSection (ünite accordion + inline player)
 *   NONUNIT  → ContentSection (tek akış)
 *
 * Tüm durumlarda gösterilir (eğer veri varsa):
 *   Açıklama, Sınav, Anket, Sertifika, Sınıf Arkadaşları, Kaynaklar
 *
 * Access modes:
 *   catalog   → Hero + Açıklama + Kaynaklar + "Başvur" sidebar
 *   pending   → Hero + Açıklama + Onay Bekleniyor empty state
 *   rejected  → Hero + Açıklama + Reddedildi empty state
 *   cancelled → Hero + Açıklama + İptal Edildi empty state
 *   trainee / completing → Full layout
 *   + Tarih yoksa (startDate/endDate boş): DatePendingState (HeroSection'sız)
 */
export default function ClientEducationDetailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const educationId = params.get("id") ?? "";

  const [loading, setLoading] = useState(true);
  const [education, setEducation] = useState<EducationDetailDto | null>(null);
  const [examAttempts, setExamAttempts] = useState<ExamAttemptDto[]>([]);
  const [certificate, setCertificate] = useState<CertificateDto | null>(null);
  const [autoSelectTrigger, setAutoSelectTrigger] = useState(0);

  async function refreshDetail() {
    if (!educationId) return;
    try {
      const fresh = await clientEducationDetailApi.getDetail(educationId);
      setEducation(fresh);
    } catch {
      /* ignore */
    }
  }

  const refetchCertificate = useCallback(async () => {
    if (!educationId) return null;
    try {
      const detail = await clientEducationDetailApi.getDetail(educationId);
      const enrollment = detail.enrollment ?? null;
      if (
        !enrollment ||
        enrollment.status !== EnrollmentStatus.COMPLETED
      ) {
        return null;
      }
      const cert = await clientEducationDetailApi.getMyCertificate(
        educationId,
        enrollment.id,
      );
      setEducation(detail);
      setExamAttempts(enrollment.examAttempts ?? []);
      setCertificate(cert);
      return cert;
    } catch {
      return null;
    }
  }, [educationId]);

  useEffect(() => {
    if (!educationId) {
      navigate("/dashboard/client-educations");
      return;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const detail = await clientEducationDetailApi.getDetail(educationId);
        if (cancelled) return;

        const enrollment = detail.enrollment ?? null;
        const needsCert =
          !!enrollment && enrollment.status === EnrollmentStatus.COMPLETED;

        const cert =
          needsCert && enrollment
            ? await clientEducationDetailApi.getMyCertificate(
                educationId,
                enrollment.id,
              )
            : null;

        if (cancelled) return;
        setEducation(detail);
        setExamAttempts(enrollment?.examAttempts ?? []);
        setCertificate(cert);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403) {
          toast.error(t("clientEducationDetail.errors.noAccess"));
          navigate("/dashboard/client-educations");
          return;
        }
        if (status === 404) {
          setEducation(null);
        } else {
          toast.error(t("clientEducationDetail.errors.loadFailed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [educationId, navigate]);

  // Pencere/sekme yeniden odaklandığında detay'ı tazele.
  // Senaryo: admin "Yayınla"ya bastıktan sonra kursiyer sekmesi geçmişten
  // gelirken status'u progression'a uğramamış (stale) görüyor — focus ile
  // refetch ederek "halen onay bekliyor" yanlış uyarısını ortadan kaldırırız.
  useEffect(() => {
    if (!educationId) return;
    const onFocus = () => {
      void refreshDetail();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refreshDetail();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationId]);

  // Sertifika hazırlanıyor durumunda otomatik polling.
  // Enrollment COMPLETED olmasına rağmen sertifika henüz hazır değilse
  // sayfayı yenilemeye gerek kalmadan periyodik olarak yeniden çekeriz.
  const certificatePollAttemptsRef = useRef(0);
  useEffect(() => {
    const enrollment = education?.enrollment ?? null;
    if (!enrollment || enrollment.status !== EnrollmentStatus.COMPLETED) {
      certificatePollAttemptsRef.current = 0;
      return;
    }
    if (certificate) {
      certificatePollAttemptsRef.current = 0;
      return;
    }
    if (certificatePollAttemptsRef.current >= 12) return;
    const timer = setTimeout(() => {
      certificatePollAttemptsRef.current += 1;
      void refetchCertificate();
    }, 5000);
    return () => clearTimeout(timer);
  }, [education, certificate, refetchCertificate]);

  function handleJump(sectionId: string) {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

async function handleReapply() {
    if (!education) return;
    try {
      await apiClient.post(
        `/api/client/educations/${education.id}/enrollments/request`,
      );
      toast.success(t("clientEducationDetail.errors.applySuccess"));
      const fresh = await clientEducationDetailApi.getDetail(education.id);
      setEducation(fresh);
    } catch {
      toast.error(t("clientEducationDetail.errors.applyFailed"));
    }
  }

  function handleCtaActivate(action: CtaDescriptor["action"]) {
    if (!education) return;
    switch (action.kind) {
      case "reapply":
        void handleReapply();
        break;
      case "joinVirtualClassroom": {
        const vc = education.virtualClassrooms?.find(
          (v) => v.id === action.classroomId,
        );
        // Backend'in kurduğu tam URL'i tercih et; yoksa (eski kayıt) room
        // adından üret. Bu, env eksikse localhost/meet.jit.si'ye düşmeyi önler.
        const joinUrl =
          vc?.jitsiUrl ??
          (vc?.jitsiRoomName ? buildJitsiRoomUrl(vc.jitsiRoomName) : null);
        if (joinUrl) {
          window.open(joinUrl, "_blank", "noopener");
        } else {
          handleJump("virtualClassrooms");
        }
        break;
      }
      case "scrollTo":
        handleJump(action.sectionId);
        break;
      case "continueContent":
        handleJump("content");
        setAutoSelectTrigger((x) => x + 1);
        break;
      case "goExam":
        navigate(`/dashboard/client-exams/take/${education.id}`);
        break;
      case "goSurvey":
        // ClientPollsPage location.state.educationId okuyup modalı otomatik açar
        navigate("/dashboard/client-polls", {
          state: {
            educationId: education.id,
            pollTitle: education.name,
          },
        });
        break;
      case "openCertificate":
        handleJump("certificate");
        if (!certificate) {
          void refetchCertificate();
        }
        break;
      default:
        break;
    }
  }

  /** Tüm state'lerde kullanılan üst bar (back + breadcrumb). */
  const renderTopBar = (title?: string) => (
    <div className={styles.topBar}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate(-1)}
        aria-label={t("clientEducationDetail.back")}
      >
        <LeftChevron width={18} height={18} />
      </button>
      <button
        type="button"
        className={styles.breadcrumbLink}
        onClick={() => navigate("/dashboard/client-educations")}
      >
        {t("clientEducationDetail.breadcrumbHome")}
      </button>
      <span className={styles.breadcrumbSep}>/</span>
      <span className={styles.breadcrumbCurrent}>{title ?? t("clientEducationDetail.title")}</span>
    </div>
  );

  const renderWithLayout = (content: React.ReactNode, title?: string) => (
    <DashboardLayout>
      <Header title={t("clientEducationDetail.title")} />
      <div className={styles.page}>
        {renderTopBar(title)}
        {content}
      </div>
    </DashboardLayout>
  );

  // ---------- Loading / Not Found ----------
  if (loading) {
    return renderWithLayout(
      <div className={styles.loading}>{t("loading")}</div>,
    );
  }

  if (!education) {
    return renderWithLayout(
      <div className={styles.empty}>{t("educationCatalog.errors.notFound")}</div>,
    );
  }

  const enrollment = education.enrollment ?? null;
  let accessMode = resolveAccessMode(enrollment);

  // "Eğitimde yayınla demeden kursiyer eğitim içindeki bilgileri görmesin":
  // Eğitim henüz kursiyere yayına alınmadıysa (backend isContentPublished=false —
  // admin "Yayınla" demedi / talep toplama hâlâ açık), kursiyer ENROLLED veya
  // içerik sürecinde olsa bile eğitime "düşmemiştir"; başvuru/onay süreci
  // görünümünde kalır ve içerik (chapter/objective, sanal sınıf, sınav vb.)
  // gösterilmez. Backend içeriği zaten strip'ler; burada da tam içerik
  // layout'una düşmeyi engelliyoruz.
  if (
    education.isContentPublished === false &&
    (accessMode === "trainee" || accessMode === "completing")
  ) {
    accessMode = "pending";
  }

  // ---------- Tarih Bekleniyor ----------
  // Eğitimin kendi tarih alanları boş olsa bile, kursiyerin atandığı bir sanal
  // sınıfın (backend taraflı `participants: some(userID)` ile zaten filtrelenmiş)
  // startDate/endDate'i varsa tarih belirlenmiş kabul edilir.
  const hasClassDates = (education.virtualClassrooms ?? []).some(
    (vc) => !!vc.startDate || !!vc.endDate,
  );
  const isDatePending =
    !education.startDate && !education.endDate && !hasClassDates;
  if (
    isDatePending &&
    accessMode !== "pending" &&
    accessMode !== "rejected" &&
    accessMode !== "cancelled"
  ) {
    // Talep toplama penceresi: flag true + bugün aralıkta
    const startOfDayMs = (v: string | undefined | null) => {
      if (!v) return null;
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    };
    const _today = new Date();
    const todayMs = new Date(
      _today.getFullYear(),
      _today.getMonth(),
      _today.getDate(),
    ).getTime();
    const reqStartMs = startOfDayMs(education.requestCollectionStartDate);
    const reqEndMs = startOfDayMs(education.requestCollectionEndDate);
    const hasReqWindow = reqStartMs != null || reqEndMs != null;
    const withinReqWindow =
      (reqStartMs == null || todayMs >= reqStartMs) &&
      (reqEndMs == null || todayMs <= reqEndMs);
    const requestCollectionOpen =
      education.isAcceptingRequestCollection === true &&
      hasReqWindow &&
      withinReqWindow;

    const pendingExtra = enrollment ? (
      <Chip type="warning" variant="filled" size="medium">
        Talep Gönderildi
      </Chip>
    ) : requestCollectionOpen ? (
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleReapply}
      >
        Talep Et
      </Button>
    ) : undefined;

    return renderWithLayout(
      <>
        {education.description && <DescriptionSection education={education} />}
        <ContentPreviewSection education={education} />
        <DatePendingState extra={pendingExtra} />
        {/* Public dokümanlar tarih henüz atanmamış olsa da erişilebilir olabilir */}
        <ClassFilesSection educationId={education.id} />
        <ResourcesSection education={education} />
      </>,
      education.name,
    );
  }

  // ---------- CTA ----------
  // `resolveCta` enrollment=null durumunu da yönetiyor (talep toplama açıksa
  // "Başvur" primary, kapalıysa disabled + hint). Bu yüzden her zaman çağırıp
  // tek doğruluk kaynağı üzerinden besliyoruz.
  const cta = resolveCta({ education, enrollment, examAttempts });

  // Sidebar içeriği — enrollment yoksa placeholder ama daima render edilir.
  const sidebarNode = (
    <DetailSidebar
      education={education}
      enrollment={
        enrollment ??
        ({
          id: "",
          educationID: education.id,
          userID: "",
          status: 0,
          progressPercent: 0,
        } as EnrollmentDto)
      }
      cta={cta}
      onCtaActivate={handleCtaActivate}
      onJumpSection={handleJump}
    />
  );

  // ---------- Devamsızlıktan / "tamamlamadı" ----------
  // Üstte kırmızı banner; timeline tüm aşamaları "failed" gösterir; CTA gizli.
  // Açıklama ve dokümanlar erişilebilir kalmalı (geçmişe referans).
  if (accessMode === "notCompleted") {
    return renderWithLayout(
      <>
        <NotCompletedState />
        <HeroSection education={education} />
        <TrainingStages
          education={education}
          enrollment={enrollment}
          onJump={handleJump}
        />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            <DescriptionSection education={education} />
            <ClassFilesSection educationId={education.id} />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Pending / Rejected / Cancelled ----------
  // Hero + TrainingStages + sidebar + empty state + resources
  if (
    accessMode === "pending" ||
    accessMode === "rejected" ||
    accessMode === "cancelled"
  ) {
    const emptyState =
      accessMode === "pending" ? (
        <PendingApprovalState />
      ) : accessMode === "rejected" ? (
        <RejectedState onReapply={() => void handleReapply()} />
      ) : (
        <CancelledState onReapply={() => void handleReapply()} />
      );

    return renderWithLayout(
      <>
        <HeroSection education={education} />
        <TrainingStages
          education={education}
          enrollment={enrollment}
          onJump={handleJump}
        />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            {emptyState}
            <DescriptionSection education={education} />
            <ClassFilesSection educationId={education.id} />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Catalog (henüz kayıt yok) ----------
  if (accessMode === "catalog") {
    return renderWithLayout(
      <>
        <HeroSection education={education} />
        <TrainingStages
          education={education}
          enrollment={enrollment}
          onJump={handleJump}
        />
        <div className={styles.layout}>
          <aside className={styles.sidebar}>{sidebarNode}</aside>
          <main className={styles.main}>
            <ActiveStageCard
              education={education}
              enrollment={enrollment}
              cta={cta}
              onCtaActivate={handleCtaActivate}
              onJump={handleJump}
            />
            <DescriptionSection education={education} />
            <ContentPreviewSection education={education} />
            <ClassFilesSection educationId={education.id} />
            <ResourcesSection education={education} />
          </main>
        </div>
      </>,
      education.name,
    );
  }

  // ---------- Trainee / Completing — full layout ----------
  const isContentType =
    education.type === EducationType.UNIT ||
    education.type === EducationType.NONUNIT ||
    education.type === EducationType.ON_SITE ||
    education.type === EducationType.ONLINE;
  const isSessionType =
    education.type === EducationType.ON_SITE ||
    education.type === EducationType.ONLINE;
  const hasChapterContent =
    (education.chapters ?? []).some((c) => (c.objectives ?? []).length > 0);

  return renderWithLayout(
    <>
      <HeroSection education={education} />
      <TrainingStages
        education={education}
        enrollment={enrollment}
        onJump={handleJump}
      />

      <div className={styles.layout}>
        <aside className={styles.sidebar}>{sidebarNode}</aside>

        <main className={styles.main}>
          {/* Aktif Aşama — "şu an ne yapmalıyım" kartı (tipe göre dinamik) */}
          <ActiveStageCard
            education={education}
            enrollment={enrollment}
            cta={cta}
            onCtaActivate={handleCtaActivate}
            onJump={handleJump}
          />

          {/* Açıklama — tüm türlerde */}
          <DescriptionSection education={education} />

          {/* Tür bazlı ana akış */}
          {/* ON_SITE ve ONLINE: Sanal sınıf / fiziksel sınıf oturum kartları */}
          {isSessionType && (
            <VirtualClassroomsSection
              education={education}
              educationType={education.type}
              onJoin={(vc) => {
                const joinUrl =
                  vc.jitsiUrl ??
                  (vc.jitsiRoomName
                    ? buildJitsiRoomUrl(vc.jitsiRoomName)
                    : null);
                if (joinUrl) {
                  window.open(joinUrl, "_blank", "noopener");
                }
              }}
            />
          )}

          {/* Ünite/ders içerik akışı — Tüm eğitim türlerinde (UNIT/NONUNIT/
              ON_SITE/ONLINE) admin tarafından eklenmiş chapter+objective
              içerikleri kursiyere gösterilir. */}
          {(isContentType || hasChapterContent) && enrollment && (
            <ContentSection
              education={education}
              enrollment={enrollment}
              autoSelectTrigger={autoSelectTrigger}
              onRequestRefresh={refreshDetail}
              onObjectiveUpdated={(updated: ObjectiveDto) => {
                setEducation((prev) => {
                  if (!prev) return prev;
                  const chapters = (prev.chapters ?? []).map((c) => ({
                    ...c,
                    objectives: c.objectives.map((o) =>
                      o.id === updated.id ? updated : o,
                    ),
                  }));
                  const flat = chapters.flatMap((c) => c.objectives);
                  const done = flat.filter(
                    (o) =>
                      (o.completionStatus ?? o.status) ===
                      ObjectiveCompletionStatus.COMPLETED,
                  ).length;
                  const pct = flat.length
                    ? Math.round((done / flat.length) * 100)
                    : 0;
                  return {
                    ...prev,
                    chapters,
                    enrollment: prev.enrollment
                      ? { ...prev.enrollment, progressPercent: pct }
                      : prev.enrollment,
                  };
                });
              }}
            />
          )}

          {/* Yoklama — sadece session-tabanlı eğitimler için */}
          {isSessionType && enrollment && (
            <AttendanceSection education={education} enrollment={enrollment} />
          )}

          {/* Sınav — eğitimde sınav tanımlanmışsa */}
          {enrollment && education.exam?.id && (
            <ExamSection
              education={education}
              enrollment={enrollment}
              attempts={examAttempts}
              onStart={() =>
                navigate(`/dashboard/client-exams/take/${education.id}`)
              }
            />
          )}

          {/* Anket — eğitimde anket tanımlanmışsa */}
          {enrollment && education.survey && (
            <SurveySection
              education={education}
              enrollment={enrollment}
              onStart={({ done }) =>
                navigate("/dashboard/client-polls", {
                  state: {
                    educationId: education.id,
                    pollTitle: education.name,
                    status: done ? "completed" : "pending",
                  },
                })
              }
            />
          )}

          {/* Sertifika — her durumda göster, içi durumuna göre bilgi/aksiyon verir */}
          {enrollment && (
            <CertificateSection
              education={education}
              enrollment={enrollment}
              certificate={certificate}
            />
          )}

          {/* Sınıf Arkadaşları — ON_SITE / ONLINE'da ve kayıt varsa */}
          {enrollment && isSessionType && (
            <ClassmatesSection
              education={education}
              currentUserId={enrollment.userID}
            />
          )}

          {/* Public dokümanlar — tüm türlerde, kayıt olsun olmasın */}
          <ClassFilesSection educationId={education.id} />
          <ResourcesSection education={education} />
        </main>
      </div>
    </>,
    education.name,
  );
}
